import { Box } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { PaloColumn, PaloListView } from '@synergy/components-palo';
import { tokens } from '@synergy/theme-palo';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { SearchConsoleTab } from '../models/search-console-tab';
import { SearchCuratedTerm } from '../models/search-quality';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

/**
 * Ebene 3 — Kuratierung. Every term somebody pinned or hid an article for, with
 * how many rules it carries. The rules themselves are made and undone in the
 * explanation of that very search; this list is the overview the concept asks
 * for, because a growing number of curated terms is a finding of its own.
 */
export const SearchCurationView = () => {
    const dispatch = useThunkDispatch();
    const navigate = useNavigate();
    const [terms, setTerms] = useState<SearchCuratedTerm[]>([]);
    const [requesting, setRequesting] = useState(true);

    useEffect(() => {
        let cancelled = false;
        void dispatch(async (_dispatch, _getState, { api }) => {
            const result = await api.searchQuality.getCurations();
            if (cancelled) {
                return;
            }
            setTerms(result.success ? result.result.terms : []);
            setRequesting(false);
        });
        return () => {
            cancelled = true;
        };
    }, [dispatch]);

    const openExplanation = (entry: SearchCuratedTerm) => {
        navigate({ pathname: `../${SearchConsoleTab.Explain}`, search: `?term=${encodeURIComponent(entry.term)}` });
    };

    const columns: PaloColumn<SearchCuratedTerm>[] = [
        {
            key: 'term',
            type: 'name',
            head: <FormattedMessage id="app.searchConsole.column.term" />,
            cell: entry => <Box sx={{ fontWeight: 550 }}>{entry.term}</Box>,
        },
        {
            key: 'pins',
            head: <FormattedMessage id="app.searchConsole.curation.column.pins" />,
            align: 'right',
            variant: 'amount',
            cell: entry => String(entry.pins),
        },
        {
            key: 'hides',
            head: <FormattedMessage id="app.searchConsole.curation.column.hides" />,
            align: 'right',
            variant: 'amount',
            cell: entry => String(entry.hides),
        },
        {
            key: 'changedAt',
            type: 'date',
            head: <FormattedMessage id="app.searchConsole.curation.column.changedAt" />,
            cell: entry => new Date(entry.changedAt).toLocaleDateString('de-AT'),
        },
    ];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <Box sx={{ fontSize: '12px', color: tokens.color.ink2 }}>
                <FormattedMessage id="app.searchConsole.curation.intro" />
            </Box>
            <PaloListView
                embedded={true}
                columns={columns}
                items={terms}
                getRowId={entry => entry.term}
                requesting={requesting}
                emptyLabel={<FormattedMessage id="app.searchConsole.curation.empty" />}
                onRowClick={openExplanation}
            />
        </Box>
    );
};
